import { faArrowsRotate } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { EmissionEvent } from "../../apiClient/generated";
import { ALLOWED_FILE_EXTENSIONS } from "../../constants/globals";
import { useEmissionEventsApiClient } from "../../hooks";
import { useFileUploader } from "../FileUploadField/hooks";
import { FileItemDisplay } from "../FileUploadField/ui";
import { LoadingIndicator } from "../MapV2/ui/LoadingIndicator";
import UploadDropdown from "../UploadDropdown";

const useEventFileUploader = (
    emissonEvent: EmissionEvent,
    callback: () => void,
) => {
    const apiClient = useEmissionEventsApiClient();

    const createUpload = useCallback(
        async (file: string) => {
            return await apiClient.emissioneventsFilesCreate({
                id: emissonEvent.id,
                eventFileCreateRequest: { file, event: emissonEvent.id },
            });
        },
        [apiClient, emissonEvent],
    );

    const markAsComplete = useCallback(
        async (fileId: string) => {
            await apiClient.emissioneventsFilesUpdate({
                id: emissonEvent.id,
                eventFileOperationRequest: { fileId },
            });
        },
        [apiClient, emissonEvent],
    );

    const deleteUpload = useCallback(
        async (fileId: string) => {
            return await apiClient.emissioneventsFilesDestroy({
                id: emissonEvent.id,
                fileId,
            });
        },
        [apiClient, emissonEvent],
    );

    return useFileUploader(
        createUpload,
        markAsComplete,
        deleteUpload,
        callback,
    );
};

interface EventFilesProps {
    event: EmissionEvent;
    readonly?: boolean;
}

export const EventFiles = (props: EventFilesProps) => {
    const queryClient = useQueryClient();
    const apiClient = useEmissionEventsApiClient();
    const filesQuery = useQuery({
        queryKey: ["eventFiles", props.event.id],
        queryFn: async () => {
            return await apiClient.emissioneventsFilesList({
                id: props.event.id,
            });
        },
        refetchOnWindowFocus: true,
        staleTime: 0,
        enabled: !!props.event.id,
    });

    const uploadedFilenames = useMemo(() => {
        return filesQuery.data?.map((i) => i.file);
    }, [filesQuery.data]);

    const dataUploader = useEventFileUploader(props.event, () =>
        queryClient.invalidateQueries({
            queryKey: ["eventFiles", props.event.id],
        }),
    );

    // When files are added to dropzone
    const onDropFiles = (files: FileList) => {
        if (!files) return;
        const newFiles = [...files];
        const filesToUpload = newFiles.filter(
            (f) => !uploadedFilenames.includes(f.name),
        );
        dataUploader.addFiles(filesToUpload);
    };

    const deleteFileMutation = useMutation({
        mutationKey: ["eventFiles", props.event.id],
        mutationFn: (fileId: string) => {
            return apiClient.emissioneventsFilesDestroy({
                id: props.event.id,
                fileId,
            });
        },
        onSuccess: () => {
            // Invalidate and refetch the files query
            queryClient.invalidateQueries({
                queryKey: ["eventFiles", props.event.id],
            });
        },
    });

    return (
        <div className="flex flex-col gap-2">
            {!props.readonly && (
                <UploadDropdown
                    accept={ALLOWED_FILE_EXTENSIONS.join(",")}
                    onChange={onDropFiles}
                    multiple
                />
            )}
            {Object.values(dataUploader.uploadState)
                .filter((u) => u.status !== "success")
                .map((upload) => (
                    <FileItemDisplay
                        key={upload.file?.name}
                        filename={upload.file?.name}
                        extraInfo={
                            upload.status === "uploading" &&
                            upload.loaded &&
                            upload.total ? (
                                `${((upload.loaded / upload.total) * 100).toFixed(1)} %`
                            ) : upload.status === "uploading" &&
                              !upload.loaded &&
                              !upload.total ? (
                                <FontAwesomeIcon
                                    icon={faArrowsRotate}
                                    className="w-4 animate-spin"
                                />
                            ) : undefined
                        }
                        error={upload.error}
                        scanComplete={false}
                        virusDetected={false}
                        onDelete={() =>
                            dataUploader.removeFile(upload.file?.name)
                        }
                    />
                ))}
            {filesQuery.isLoading && <LoadingIndicator />}
            {filesQuery.data?.map((file) => (
                <FileItemDisplay
                    key={file.id}
                    filename={file.filename}
                    extraInfo={
                        file.fileStatus === "WAITING_AV_CHECK"
                            ? "Waiting AV check..."
                            : undefined
                    }
                    downloadUrl={file.file}
                    scanComplete={["READY", "VIRUS_DETECTED"].includes(
                        file.fileStatus,
                    )}
                    virusDetected={["VIRUS_DETECTED"].includes(file.fileStatus)}
                    onDelete={() => deleteFileMutation.mutate(file.id)}
                    readonly={props.readonly}
                />
            ))}
        </div>
    );
};
